import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixBlogV3Category from './blog-v3-category-categories.http.js';
// @ts-ignore
import { transformSDKImageToRESTImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformSDKPageURLToRESTPageURL } from '@wix/sdk-runtime/transformations/page-url';
import { transformRESTPageURLToSDKPageURL } from '@wix/sdk-runtime/transformations/page-url';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface Category {
  /**
   * Category ID.
   * @immutable
   * @maxLength 38
   */
  _id?: string;
  /**
   * Category label. Displayed in the Category Menu.
   * @maxLength 35
   */
  label?: string;
  /**
   * Number of posts in the category.
   * @readonly
   */
  postCount?: number;
  /**
   * The `url` of the page that lists every post with the specified category.
   * @readonly
   */
  url?: string;
  /**
   * Category description.
   * @maxLength 500
   */
  description?: string | null;
  /**
   * Category title.
   * @maxLength 200
   * @deprecated Category title.
   * @targetRemovalDate 2025-07-16
   */
  title?: string;
  /**
   * Position of the category in the [Category Menu](https://support.wix.com/en/article/wix-blog-adding-and-customizing-a-category-menu).
   * Categories are displayed in ascending order. Categories with a position of `-1` appear at the end of the sequence.
   *
   * Default: `-1`
   */
  displayPosition?: number | null;
  /**
   * ID of the category's translations. All translations of a single category share the same `translationId`.
   * @format GUID
   */
  translationId?: string | null;
  /**
   * Category language.
   *
   * 2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.
   * @immutable
   */
  language?: string | null;
  /**
   * Part of a category's URL that refers to a specific category. For example, the slug of `https:/example.com/blog/category/famous-cats` is `famous-cats`.
   * @maxLength 100
   */
  slug?: string;
  /** SEO data. */
  seoData?: SeoSchema;
  /** Category cover image. */
  coverImage?: string;
  /**
   * Date and time the Category was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

/**
 * The SEO schema object contains data about different types of meta tags. It makes sure that the information about your page is presented properly to search engines.
 * The search engines use this information for ranking purposes, or to display snippets in the search results.
 * This data will override other sources of tags (for example patterns) and will be included in the <head> section of the HTML document, while not being displayed on the page itself.
 */
export interface SeoSchema {
  /** SEO tag information. */
  tags?: Tag[];
  /** SEO general settings. */
  settings?: Settings;
}

export interface Keyword {
  /** Keyword value. */
  term?: string;
  /** Whether the keyword is the main focus keyword. */
  isMain?: boolean;
  /**
   * The source that added the keyword terms to the SEO settings.
   * @maxLength 1000
   */
  origin?: string | null;
}

export interface Tag {
  /**
   * SEO tag type.
   *
   *
   * Supported values: `title`, `meta`, `script`, `link`.
   */
  type?: string;
  /**
   * A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.
   * For example: `{"name": "description", "content": "the description itself"}`.
   */
  props?: Record<string, any> | null;
  /** SEO tag metadata. For example, `{"height": 300, "width": 240}`. */
  meta?: Record<string, any> | null;
  /** SEO tag inner content. For example, `<title> inner content </title>`. */
  children?: string;
  /** Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages). */
  custom?: boolean;
  /** Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines. */
  disabled?: boolean;
}

export interface Settings {
  /**
   * Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.
   *
   *
   * Default: `false` (automatical redirect is enabled).
   */
  preventAutoRedirect?: boolean;
  /**
   * User-selected keyword terms for a specific page.
   * @maxSize 5
   */
  keywords?: Keyword[];
}

export interface FocalPoint {
  /** X-coordinate of the focal point. */
  x?: number;
  /** Y-coordinate of the focal point. */
  y?: number;
  /** crop by height */
  height?: number | null;
  /** crop by width */
  width?: number | null;
}

export interface CategoryTranslation {
  /**
   * Category ID.
   * @format GUID
   */
  _id?: string;
  /**
   * Label displayed in the categories menu on the site.
   * @maxLength 100
   */
  label?: string | null;
  /**
   * Language of the category.
   * @format LANGUAGE_TAG
   */
  language?: string | null;
  /** URL of this category page. */
  url?: string;
}

export interface InitialCategoriesCopied {
  /** Number of categories copied. */
  count?: number;
}

export interface CreateCategoryRequest {
  /** Category info. */
  category: Category;
  /**
   * List of additional category fields to include in the response. By default
   * only the category’s base fields are returned. Base fields are all category fields that don't appear in the fieldset enum.
   *
   * To retrieve a field, pass the relevant fieldset in the enum in the `fieldsets` array. For example, add the `URL` fieldset to `fieldsets` to
   * retrieve the url field in the response in addition to the category’s base fields.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export enum Field {
  UNKNOWN = 'UNKNOWN',
  /** Includes category URL. */
  URL = 'URL',
  /** Includes SEO data. */
  SEO = 'SEO',
}

/** @enumType */
export type FieldWithLiterals = Field | 'UNKNOWN' | 'URL' | 'SEO';

export interface CreateCategoryResponse {
  /** Category info. */
  category?: Category;
}

export interface BulkCreateCategoriesRequest {
  /**
   * Categories to create.
   * @minSize 1
   * @maxSize 50
   */
  categories?: Category[];
  /** Whether to return the full created category entities in the response. */
  returnFullEntity?: boolean;
  /**
   * List of additional category fields to include in the response. By default
   * only the category’s base fields are returned. Base fields are all category fields that don't appear in the fieldset enum.
   *
   * To retrieve a field, pass the relevant fieldset in the enum in the `fieldsets` array. For example, add the `URL` fieldset to `fieldsets` to
   * retrieve the url field in the response in addition to the category’s base fields.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export interface BulkCreateCategoriesResponse {
  /** Categories created by bulk action. */
  results?: BulkCategoryResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkCategoryResult {
  /** Bulk actions metadata for category. */
  itemMetadata?: ItemMetadata;
  /** Optional created category. */
  item?: Category;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateCategoriesRequest {
  /**
   * Categories to update.
   * @minSize 1
   * @maxSize 100
   */
  categories?: MaskedCategory[];
  /** Whether to return the full created category entities in the response. */
  returnFullEntity?: boolean;
  /**
   * List of additional category fields to include in the response. By default
   * only the category’s base fields are returned. Base fields are all category fields that don't appear in the fieldset enum.
   *
   * To retrieve a field, pass the relevant fieldset in the enum in the `fieldsets` array. For example, add the `URL` fieldset to `fieldsets` to
   * retrieve the url field in the response in addition to the category’s base fields.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export interface MaskedCategory {
  /** Category */
  category?: Category;
  /** Field mask of fields to update. */
  fieldMask?: string[];
}

export interface BulkUpdateCategoriesResponse {
  /** Categories updated by bulk action. */
  results?: BulkCategoryResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface UpdateCategoryRequest {
  /** Category info. */
  category: Category;
  /**
   * List of additional category fields to include in the response. By default
   * only the category’s base fields are returned. Base fields are all category fields that don't appear in the fieldset enum.
   *
   * To retrieve a field, pass the relevant fieldset in the enum in the `fieldsets` array. For example, add the `URL` fieldset to `fieldsets` to
   * retrieve the url field in the response in addition to the category’s base fields.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export interface UpdateCategoryResponse {
  /** Category info. */
  category?: Category;
}

export interface GetCategoriesCountByLanguageRequest {}

export interface GetCategoriesCountByLanguageResponse {
  /** The language and it's count. */
  categoriesLanguageCount?: CategoryLanguageCount[];
}

export interface CategoryLanguageCount {
  /**
   * 2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.
   * @format LANGUAGE_TAG
   */
  languageCode?: string | null;
  /** The count of Categories for the language. */
  categoryCount?: number | null;
}

export interface GetCategoryRequest {
  /**
   * Category ID.
   * @minLength 1
   * @maxLength 38
   */
  categoryId: string;
  /**
   * List of additional category fields to include in the response. By default
   * only the category’s base fields are returned. Base fields are all category fields that don't appear in the fieldset enum.
   *
   * To retrieve a field, pass the relevant fieldset in the enum in the `fieldsets` array. For example, add the `URL` fieldset to `fieldsets` to
   * retrieve the url field in the response in addition to the category’s base fields.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export interface GetCategoryResponse {
  /** Category info. */
  category?: Category;
}

export interface GetCategoryBySlugRequest {
  /**
   * Slug of the category to retrieve.
   * @minLength 1
   * @maxLength 100
   */
  slug: string;
  /**
   * List of additional category fields to include in the response. By default
   * only the category’s base fields are returned. Base fields are all category fields that don't appear in the fieldset enum.
   *
   * To retrieve a field, pass the relevant fieldset in the enum in the `fieldsets` array. For example, add the `URL` fieldset to `fieldsets` to
   * retrieve the url field in the response in addition to the category’s base fields.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
  /**
   * 2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.
   * Language of the category to retrieve.
   * @format LANGUAGE_TAG
   */
  language?: string | null;
}

export interface GetCategoryBySlugResponse {
  /** Category info. */
  category?: Category;
}

export interface ListCategoriesRequest {
  /** Pagination options. */
  paging?: BlogPaging;
  /**
   * List of additional category fields to include in the response. By default
   * only the category’s base fields are returned. Base fields are all category fields that don't appear in the fieldset enum.
   *
   * To retrieve a field, pass the relevant fieldset in the enum in the `fieldsets` array. For example, add the `URL` fieldset to `fieldsets` to
   * retrieve the url field in the response in addition to the category’s base fields.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
  /**
   * Language filter.
   *
   * 2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.
   * Pass a language to only receive categories that are in that language.
   * If omitted, categories in all languages are returned.
   * @format LANGUAGE_TAG
   */
  language?: string | null;
}

export interface BlogPaging {
  /**
   * Number of items to skip in the current sort order.
   *
   *
   * Default: `0`
   */
  offset?: number;
  /**
   * Number of items to return.
   *
   * Default: `50`
   *
   * Max: `100`
   * @min 1
   * @max 100
   */
  limit?: number;
  /**
   * Pointer to the next or previous page in the list of results.
   * @maxLength 2000
   */
  cursor?: string | null;
}

export interface ListCategoriesResponse {
  /** List of categories. */
  categories?: Category[];
  /** Details on the paged set of results returned. */
  metaData?: MetaData;
}

export interface MetaData {
  /** Number of items returned in this response. */
  count?: number;
  /** Requested offset. */
  offset?: number;
  /** Total number of items that match the query. */
  total?: number;
  /**
   * Pointer to the next or previous page in the list of results.
   * @maxLength 2000
   */
  cursor?: string | null;
}

export interface QueryCategoriesRequest {
  /** Query options. */
  query?: PlatformQuery;
  /**
   * List of additional category fields to include in the response. By default
   * only the category’s base fields are returned. Base fields are all category fields that don't appear in the fieldset enum.
   *
   * To retrieve a field, pass the relevant fieldset in the enum in the `fieldsets` array. For example, add the `URL` fieldset to `fieldsets` to
   * retrieve the url field in the response in addition to the category’s base fields.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export interface PlatformQuery extends PlatformQueryPagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 3
   */
  sort?: Sorting[];
}

/** @oneof */
export interface PlatformQueryPagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /**
   * Number of items to load.
   * @max 100
   */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /**
   * Number of items to load.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 2000
   */
  cursor?: string | null;
}

export interface QueryCategoriesResponse {
  /** List of categories. */
  categories?: Category[];
  /** Details on the paged set of results returned. */
  pagingMetadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /**
   * Cursor pointing to next page in the list of results.
   * @maxLength 2000
   */
  next?: string | null;
  /**
   * Cursor pointing to previous page in the list of results.
   * @maxLength 2000
   */
  prev?: string | null;
}

export interface BulkDeleteCategoryRequest {
  /**
   * Category IDs.
   * @minSize 1
   * @maxSize 100
   * @maxLength 38
   */
  categoryIds?: string[];
  /** Should delete bypassing the trash-bin. */
  permanent?: boolean;
}

export interface BulkDeleteCategoryResponse {}

export interface DeleteCategoryRequest {
  /**
   * Category ID.
   * @minLength 1
   * @maxLength 38
   */
  categoryId: string;
}

export interface DeleteCategoryResponse {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface CategoryCreatedEnvelope {
  entity: Category;
  metadata: EventMetadata;
}

/**
 * Triggered when a category is created.
 * @permissionScope Read Blog
 * @permissionScopeId SCOPE.DC-BLOG.READ-BLOGS
 * @permissionScope Manage Blog
 * @permissionScopeId SCOPE.DC-BLOG.MANAGE-BLOG
 * @permissionScope Read Draft Blog Posts
 * @permissionScopeId SCOPE.DC-BLOG.READ-DRAFT-POSTS
 * @permissionId BLOG.READ-CATEGORY
 * @webhook
 * @eventType wix.blog.v3.category_created
 * @serviceIdentifier com.wixpress.npm.communities.platformized.blog.v3.CategoryService
 * @slug created
 */
export declare function onCategoryCreated(
  handler: (event: CategoryCreatedEnvelope) => void | Promise<void>
): void;

export interface CategoryDeletedEnvelope {
  metadata: EventMetadata;
}

/**
 * Triggered when a category is deleted.
 * @permissionScope Read Blog
 * @permissionScopeId SCOPE.DC-BLOG.READ-BLOGS
 * @permissionScope Manage Blog
 * @permissionScopeId SCOPE.DC-BLOG.MANAGE-BLOG
 * @permissionScope Read Draft Blog Posts
 * @permissionScopeId SCOPE.DC-BLOG.READ-DRAFT-POSTS
 * @permissionId BLOG.READ-CATEGORY
 * @webhook
 * @eventType wix.blog.v3.category_deleted
 * @serviceIdentifier com.wixpress.npm.communities.platformized.blog.v3.CategoryService
 * @slug deleted
 */
export declare function onCategoryDeleted(
  handler: (event: CategoryDeletedEnvelope) => void | Promise<void>
): void;

export interface CategoryUpdatedEnvelope {
  entity: Category;
  metadata: EventMetadata;
}

/**
 * Triggered when a category is updated.
 * @permissionScope Read Blog
 * @permissionScopeId SCOPE.DC-BLOG.READ-BLOGS
 * @permissionScope Manage Blog
 * @permissionScopeId SCOPE.DC-BLOG.MANAGE-BLOG
 * @permissionScope Read Draft Blog Posts
 * @permissionScopeId SCOPE.DC-BLOG.READ-DRAFT-POSTS
 * @permissionId BLOG.READ-CATEGORY
 * @webhook
 * @eventType wix.blog.v3.category_updated
 * @serviceIdentifier com.wixpress.npm.communities.platformized.blog.v3.CategoryService
 * @slug updated
 */
export declare function onCategoryUpdated(
  handler: (event: CategoryUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a category.
 * @param category - Category info.
 * @public
 * @requiredField category
 * @requiredField category.label
 * @permissionId BLOG.CREATE-CATEGORY
 * @applicableIdentity APP
 * @returns Category info.
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.CategoryService.CreateCategory
 */
export async function createCategory(
  category: NonNullablePaths<Category, `label`, 2>,
  options?: CreateCategoryOptions
): Promise<
  NonNullablePaths<
    Category,
    | `_id`
    | `label`
    | `postCount`
    | `title`
    | `slug`
    | `seoData.tags`
    | `seoData.tags.${number}.type`
    | `seoData.tags.${number}.children`
    | `seoData.tags.${number}.custom`
    | `seoData.tags.${number}.disabled`
    | `seoData.settings.preventAutoRedirect`
    | `seoData.settings.keywords`
    | `seoData.settings.keywords.${number}.term`
    | `seoData.settings.keywords.${number}.isMain`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      category: category,
      fieldsets: options?.fieldsets,
    }),
    [
      {
        transformFn: transformSDKPageURLToRESTPageURL,
        paths: [
          { path: 'category.url' },
          { path: 'category.translations.url' },
        ],
      },
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'category.coverImage' }],
      },
    ]
  );

  const reqOpts = ambassadorWixBlogV3Category.createCategory(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'category.url' },
            { path: 'category.translations.url' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'category.coverImage' }],
        },
      ])
    )?.category!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          category: '$[0]',
          fieldsets: '$[1].fieldsets',
        },
        singleArgumentUnchanged: false,
      },
      ['category', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreateCategoryOptions {
  /**
   * List of additional category fields to include in the response. By default
   * only the category’s base fields are returned. Base fields are all category fields that don't appear in the fieldset enum.
   *
   * To retrieve a field, pass the relevant fieldset in the enum in the `fieldsets` array. For example, add the `URL` fieldset to `fieldsets` to
   * retrieve the url field in the response in addition to the category’s base fields.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

/**
 * Updates a category.
 * @param _id - Category ID.
 * @public
 * @requiredField _id
 * @requiredField category
 * @permissionId BLOG.EDIT-CATEGORY
 * @applicableIdentity APP
 * @returns Category info.
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.CategoryService.UpdateCategory
 */
export async function updateCategory(
  _id: string,
  category: UpdateCategory,
  options?: UpdateCategoryOptions
): Promise<
  NonNullablePaths<
    Category,
    | `_id`
    | `label`
    | `postCount`
    | `title`
    | `slug`
    | `seoData.tags`
    | `seoData.tags.${number}.type`
    | `seoData.tags.${number}.children`
    | `seoData.tags.${number}.custom`
    | `seoData.tags.${number}.disabled`
    | `seoData.settings.preventAutoRedirect`
    | `seoData.settings.keywords`
    | `seoData.settings.keywords.${number}.term`
    | `seoData.settings.keywords.${number}.isMain`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      category: { ...category, id: _id },
      fieldsets: options?.fieldsets,
    }),
    [
      {
        transformFn: transformSDKPageURLToRESTPageURL,
        paths: [
          { path: 'category.url' },
          { path: 'category.translations.url' },
        ],
      },
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'category.coverImage' }],
      },
    ]
  );

  const reqOpts = ambassadorWixBlogV3Category.updateCategory(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'category.url' },
            { path: 'category.translations.url' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'category.coverImage' }],
        },
      ])
    )?.category!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { category: '$[1]' },
        explicitPathsToArguments: {
          'category.id': '$[0]',
          fieldsets: '$[2].fieldsets',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'category', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateCategory {
  /**
   * Category ID.
   * @immutable
   * @maxLength 38
   */
  _id?: string;
  /**
   * Category label. Displayed in the Category Menu.
   * @maxLength 35
   */
  label?: string;
  /**
   * Number of posts in the category.
   * @readonly
   */
  postCount?: number;
  /**
   * The `url` of the page that lists every post with the specified category.
   * @readonly
   */
  url?: string;
  /**
   * Category description.
   * @maxLength 500
   */
  description?: string | null;
  /**
   * Category title.
   * @maxLength 200
   * @deprecated Category title.
   * @targetRemovalDate 2025-07-16
   */
  title?: string;
  /**
   * Position of the category in the [Category Menu](https://support.wix.com/en/article/wix-blog-adding-and-customizing-a-category-menu).
   * Categories are displayed in ascending order. Categories with a position of `-1` appear at the end of the sequence.
   *
   * Default: `-1`
   */
  displayPosition?: number | null;
  /**
   * ID of the category's translations. All translations of a single category share the same `translationId`.
   * @format GUID
   */
  translationId?: string | null;
  /**
   * Category language.
   *
   * 2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.
   * @immutable
   */
  language?: string | null;
  /**
   * Part of a category's URL that refers to a specific category. For example, the slug of `https:/example.com/blog/category/famous-cats` is `famous-cats`.
   * @maxLength 100
   */
  slug?: string;
  /** SEO data. */
  seoData?: SeoSchema;
  /** Category cover image. */
  coverImage?: string;
  /**
   * Date and time the Category was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

export interface UpdateCategoryOptions {
  /**
   * List of additional category fields to include in the response. By default
   * only the category’s base fields are returned. Base fields are all category fields that don't appear in the fieldset enum.
   *
   * To retrieve a field, pass the relevant fieldset in the enum in the `fieldsets` array. For example, add the `URL` fieldset to `fieldsets` to
   * retrieve the url field in the response in addition to the category’s base fields.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

/**
 * Gets a category with the specified ID.
 * @param categoryId - Category ID.
 * @public
 * @requiredField categoryId
 * @param options - Options specifying which fields to return.
 * @permissionId BLOG.READ-CATEGORY
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.CategoryService.GetCategory
 */
export async function getCategory(
  categoryId: string,
  options?: GetCategoryOptions
): Promise<
  NonNullablePaths<
    GetCategoryResponse,
    | `category._id`
    | `category.label`
    | `category.postCount`
    | `category.title`
    | `category.slug`
    | `category.seoData.tags`
    | `category.seoData.tags.${number}.type`
    | `category.seoData.tags.${number}.children`
    | `category.seoData.tags.${number}.custom`
    | `category.seoData.tags.${number}.disabled`
    | `category.seoData.settings.preventAutoRedirect`
    | `category.seoData.settings.keywords`
    | `category.seoData.settings.keywords.${number}.term`
    | `category.seoData.settings.keywords.${number}.isMain`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    categoryId: categoryId,
    fieldsets: options?.fieldsets,
  });

  const reqOpts = ambassadorWixBlogV3Category.getCategory(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'category.url' },
            { path: 'category.translations.url' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'category.coverImage' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          categoryId: '$[0]',
          fieldsets: '$[1].fieldsets',
        },
        singleArgumentUnchanged: false,
      },
      ['categoryId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetCategoryOptions {
  /**
   * List of additional category fields to include in the response. By default
   * only the category’s base fields are returned. Base fields are all category fields that don't appear in the fieldset enum.
   *
   * To retrieve a field, pass the relevant fieldset in the enum in the `fieldsets` array. For example, add the `URL` fieldset to `fieldsets` to
   * retrieve the url field in the response in addition to the category’s base fields.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

/**
 * Gets a category with the specified slug.
 *
 * The slug is at the end of the URL of a specific category.
 * For example, if a category's URL is `https://example.com/blog/categories/famous-cats`, the slug is `famous-cats`.
 * The slug is a case-sensitive string that is derived from the category's label, unless specified otherwise.
 * @public
 * @requiredField slug
 * @param slug - Slug of the category to retrieve.
 *
 * The end of a category's URL. For example, `'https:/example.com/blog/category/{my-category-slug}'`. Case sensitive and generally based on the category `label` if not specified.
 * @param options - Options specifying which fields to return.
 * @permissionId BLOG.READ-CATEGORY
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.CategoryService.GetCategoryBySlug
 */
export async function getCategoryBySlug(
  slug: string,
  options?: GetCategoryBySlugOptions
): Promise<
  NonNullablePaths<
    GetCategoryBySlugResponse,
    | `category._id`
    | `category.label`
    | `category.postCount`
    | `category.title`
    | `category.slug`
    | `category.seoData.tags`
    | `category.seoData.tags.${number}.type`
    | `category.seoData.tags.${number}.children`
    | `category.seoData.tags.${number}.custom`
    | `category.seoData.tags.${number}.disabled`
    | `category.seoData.settings.preventAutoRedirect`
    | `category.seoData.settings.keywords`
    | `category.seoData.settings.keywords.${number}.term`
    | `category.seoData.settings.keywords.${number}.isMain`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    slug: slug,
    fieldsets: options?.fieldsets,
    language: options?.language,
  });

  const reqOpts = ambassadorWixBlogV3Category.getCategoryBySlug(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'category.url' },
            { path: 'category.translations.url' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'category.coverImage' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          slug: '$[0]',
          fieldsets: '$[1].fieldsets',
          language: '$[1].language',
        },
        singleArgumentUnchanged: false,
      },
      ['slug', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetCategoryBySlugOptions {
  /**
   * List of additional category fields to include in the response. By default
   * only the category’s base fields are returned. Base fields are all category fields that don't appear in the fieldset enum.
   *
   * To retrieve a field, pass the relevant fieldset in the enum in the `fieldsets` array. For example, add the `URL` fieldset to `fieldsets` to
   * retrieve the url field in the response in addition to the category’s base fields.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
  /**
   * 2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.
   * Language of the category to retrieve.
   * @format LANGUAGE_TAG
   */
  language?: string | null;
}

/**
 * Retrieves a list of up to 100 categories per request.
 *
 * If you pass a language filter to the method, it returns a list of up to 100 categories in the requested language.
 *
 * The categoies are displayed in order of their `displayPosition`, starting with `0`. The `displayPosition` is the position in which the categories
 * are displayed in the Category Menu page. By default, categories get added to the bottom of the Category Menu page with a `displayPosition` of `-1`.
 *
 * List Categories runs with these defaults, which you can override:
 * - `paging.limit` is `50`.
 * - `paging.offset` is `0`.
 *
 * List Categories is sorted by `displayPosition` in descending order. This cannot be overridden.
 * @public
 * @param options - Filter and paging options.
 * @permissionId BLOG.READ-CATEGORY
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.CategoryService.ListCategories
 */
export async function listCategories(
  options?: ListCategoriesOptions
): Promise<
  NonNullablePaths<
    ListCategoriesResponse,
    | `categories`
    | `categories.${number}._id`
    | `categories.${number}.label`
    | `categories.${number}.postCount`
    | `categories.${number}.title`
    | `categories.${number}.slug`
    | `categories.${number}.seoData.settings.preventAutoRedirect`
    | `metaData.count`
    | `metaData.offset`
    | `metaData.total`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    paging: options?.paging,
    fieldsets: options?.fieldsets,
    language: options?.language,
  });

  const reqOpts = ambassadorWixBlogV3Category.listCategories(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'categories.url' },
            { path: 'categories.translations.url' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'categories.coverImage' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          paging: '$[0].paging',
          fieldsets: '$[0].fieldsets',
          language: '$[0].language',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListCategoriesOptions {
  /** Pagination options. */
  paging?: BlogPaging;
  /**
   * List of additional category fields to include in the response. By default
   * only the category’s base fields are returned. Base fields are all category fields that don't appear in the fieldset enum.
   *
   * To retrieve a field, pass the relevant fieldset in the enum in the `fieldsets` array. For example, add the `URL` fieldset to `fieldsets` to
   * retrieve the url field in the response in addition to the category’s base fields.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
  /**
   * Language filter.
   *
   * 2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.
   * Pass a language to only receive categories that are in that language.
   * If omitted, categories in all languages are returned.
   * @format LANGUAGE_TAG
   */
  language?: string | null;
}

/**
 * Creates a query to retrieve a list of categories.
 *
 *
 * The Query Categories method builds a query to retrieve a list of up to 100 categories per language, and returns a [`CategoriesQueryBuilder`](https://dev.wix.com/docs/sdk/backend-modules/blog/categories/categories-query-builder/ascending) object.
 *
 * The response contains the query definition, which is typically used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/blog/categories/categories-query-builder/find) method.
 *
 * You can refine the query by chaining `CategoriesQueryBuilder` methods to the query. `CategoriesQueryBuilder` methods enable you to sort, filter, and control the results that Query Categories returns. Any methods chained to the Query Categories method are applied in the order that they are called.
 *
 * Query Categories runs with these `CategoriesQueryBuilder` defaults, which you can override.
 * - [`limit(100)`](https://dev.wix.com/docs/sdk/backend-modules/blog/categories/categories-query-builder/limit)
 * - [`ascending(displayPosition)`](https://dev.wix.com/docs/sdk/backend-modules/blog/categories/categories-query-builder/ascending)
 *
 * The following `CategoriesQueryBuilder` methods are supported for Query Categories. For a full description of the `Categories` object, see the response for the [`items`](https://dev.wix.com/docs/sdk/backend-modules/blog/categories/categories-query-result/items) property in [`CategoriesQueryResult`](https://www.wix.com/velo/reference/wix-blog-backend/categories/categoriesqueryresult).
 * @public
 * @param options - Options specifying which fields to return.
 * @permissionId BLOG.READ-CATEGORY
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.CategoryService.QueryCategories
 */
export function queryCategories(
  options?: QueryCategoriesOptions
): CategoriesQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    Category,
    'OFFSET',
    QueryCategoriesRequest,
    QueryCategoriesResponse
  >({
    func: async (payload: QueryCategoriesRequest) => {
      const reqOpts = ambassadorWixBlogV3Category.queryCategories({
        ...payload,
        ...(options ?? {}),
      });

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryCategoriesRequest['query']) => {
      const args = [query, options] as [
        QueryCategoriesRequest['query'],
        QueryCategoriesOptions
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryCategoriesResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTPageURLToSDKPageURL,
            paths: [
              { path: 'categories.url' },
              { path: 'categories.translations.url' },
            ],
          },
          {
            transformFn: transformRESTImageToSDKImage,
            paths: [{ path: 'categories.coverImage' }],
          },
        ])
      );

      return {
        items: transformedData?.categories,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'OFFSET',
    transformationPaths: {},
  });
}

export interface QueryCategoriesOptions {
  /**

   * @maxSize 10
     */
  fieldsets?: FieldWithLiterals[] | undefined;
}

interface QueryOffsetResult {
  currentPage: number | undefined;
  totalPages: number | undefined;
  totalCount: number | undefined;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface CategoriesQueryResult extends QueryOffsetResult {
  items: Category[];
  query: CategoriesQueryBuilder;
  next: () => Promise<CategoriesQueryResult>;
  prev: () => Promise<CategoriesQueryResult>;
}

export interface CategoriesQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | 'label'
      | 'postCount'
      | 'title'
      | 'displayPosition'
      | 'translationId'
      | 'language'
      | 'slug',
    value: any
  ) => CategoriesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | 'label'
      | 'postCount'
      | 'title'
      | 'displayPosition'
      | 'translationId'
      | 'language'
      | 'slug',
    value: any
  ) => CategoriesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName: 'label' | 'postCount' | 'title' | 'displayPosition',
    value: any
  ) => CategoriesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName: 'label' | 'postCount' | 'title' | 'displayPosition',
    value: any
  ) => CategoriesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName: 'label' | 'postCount' | 'title' | 'displayPosition',
    value: any
  ) => CategoriesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName: 'label' | 'postCount' | 'title' | 'displayPosition',
    value: any
  ) => CategoriesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: 'label' | 'title',
    value: string
  ) => CategoriesQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | 'label'
      | 'postCount'
      | 'title'
      | 'displayPosition'
      | 'translationId'
      | 'language'
      | 'slug',
    value: any
  ) => CategoriesQueryBuilder;
  exists: (
    propertyName: 'label' | 'title' | 'translationId' | 'language' | 'slug',
    value: boolean
  ) => CategoriesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | 'label'
      | 'postCount'
      | 'title'
      | 'displayPosition'
      | 'language'
      | 'slug'
    >
  ) => CategoriesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | 'label'
      | 'postCount'
      | 'title'
      | 'displayPosition'
      | 'language'
      | 'slug'
    >
  ) => CategoriesQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => CategoriesQueryBuilder;
  /** @param skip - Number of items to skip in the query results before returning the results. */
  skip: (skip: number) => CategoriesQueryBuilder;
  find: () => Promise<CategoriesQueryResult>;
}

/**
 * @hidden
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.CategoryService.QueryCategories
 * @requiredField query
 * @returns A `CategoriesQueryBuilder` object that contains the refined query.
 */
export async function typedQueryCategories(
  query: CategoryQuery,
  options?: QueryCategoriesOptions
): Promise<
  NonNullablePaths<
    QueryCategoriesResponse,
    | `categories`
    | `categories.${number}._id`
    | `categories.${number}.label`
    | `categories.${number}.postCount`
    | `categories.${number}.title`
    | `categories.${number}.slug`
    | `categories.${number}.seoData.settings.preventAutoRedirect`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: query,
    ...options,
  });

  const reqOpts = ambassadorWixBlogV3Category.queryCategories(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'categories.url' },
            { path: 'categories.translations.url' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'categories.coverImage' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CategoryQuerySpec extends QuerySpec {
  paging: 'offset';
  wql: [
    {
      fields: ['_id'];
      operators: ['$eq', '$in', '$ne', '$nin'];
      sort: 'BOTH';
    },
    {
      fields: ['label', 'title'];
      operators: [
        '$eq',
        '$exists',
        '$gt',
        '$gte',
        '$in',
        '$lt',
        '$lte',
        '$ne',
        '$startsWith'
      ];
      sort: 'BOTH';
    },
    {
      fields: ['displayPosition', 'postCount'];
      operators: ['$eq', '$gt', '$gte', '$in', '$lt', '$lte', '$ne'];
      sort: 'BOTH';
    },
    {
      fields: ['translationId'];
      operators: ['$eq', '$exists', '$in', '$ne'];
      sort: 'NONE';
    },
    {
      fields: ['language'];
      operators: ['$eq', '$exists', '$in', '$ne'];
      sort: 'BOTH';
    },
    {
      fields: ['slug'];
      operators: ['$eq', '$exists', '$in', '$ne'];
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  Category,
  CategoryQuerySpec
>;
export type CategoryQuery = {
  /** 
  Paging options to limit and skip the number of items.  
  */
  paging?: {
    /** 
  Number of items to load. 
  @max: 100 
  */
    limit?: NonNullable<CommonQueryWithEntityContext['paging']>['limit'] | null;
    /** 
  Number of items to skip in the current sort order.  
  */
    offset?:
      | NonNullable<CommonQueryWithEntityContext['paging']>['offset']
      | null;
  };

  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 3 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<Category, CategoryQuerySpec, CategoryQuery>(),
  },
};

/**
 * Deletes a category.
 * @param categoryId - Category ID.
 * @public
 * @requiredField categoryId
 * @permissionId BLOG.DELETE-CATEGORY
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.CategoryService.DeleteCategory
 */
export async function deleteCategory(categoryId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    categoryId: categoryId,
  });

  const reqOpts = ambassadorWixBlogV3Category.deleteCategory(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { categoryId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['categoryId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
